import type { ChangeEvent, FC, ReactElement } from 'react';
import { useRef, useState } from 'react';

import { NetworkError } from '../../../api';
import type { EnvConfigType } from '../../../api/fetchers/env/types';
import { postNickName } from '../../../api/fetchers/nickName';
import { postPromoScreenName } from '../../../api/fetchers/promotions/fetcher';
import { ScreenNameConstants } from '../../../config/CoreConstants';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { configActions } from '../../../store/slices/config/slice';
import { popupActions } from '../../../store/slices/popup/slice';
import { Button, ButtonVariant } from '../../controls/button/Button';
import { BaseInput } from '../../inputs/baseInput/BaseInput';
import { Loader } from '../../loader';
import { L12nText } from '../../text/L12nText';
import { PopupType } from '../types';

import { BlockingModalContent } from './BlockingModalContent';

import styles from './popupContent.module.css';

export const NicknameEnterConnected: FC = (): ReactElement => {
  const dispatch = useCoreDispatch();
  const formRef = useRef<HTMLFormElement>(null);
  const field = useRef<HTMLInputElement>(null);

  const [isLoading, setLoad] = useState(false);
  const [error, setError] = useState('');
  const [errMsgDynamicValues, setErrMsgDynamicValues] = useState<Array<string>>([]);

  const baseUrl = useConfigSlice.baseUrl();
  const JSESSIONID = useConfigSlice.JSESSIONID() as string;
  const env = useConfigSlice.env() as EnvConfigType;
  const jwtToken = useConfigSlice.jwtToken() as string;
  const showNicknameDisclaimer = useConfigSlice.casinoConfig()?.showNicknameDisclaimer;
  const tableId = useConfigSlice.tableId() ?? '';

  const submitForm = async (): Promise<void> => {
    const isValid = formRef.current?.reportValidity();

    if (isValid) {
      setLoad(true);
      const { screenName } = formRef.current as HTMLFormElement;

      postNickName({
        url: `${baseUrl}/api/user/screenName`,
        JSESSIONID: JSESSIONID,
        screenName: screenName.value.trim() as string,
        tableId: tableId,
      })
        .then(() => {
          dispatch(popupActions.dismissPopup(PopupType.NicknameEnter));
          dispatch(configActions.setScreenName(screenName.value.trim()));
          void postPromoScreenName({ envPromo: env.promoService, jwtToken: jwtToken });
        })
        .catch((error) => {
          setLoad(false);

          if (error instanceof NetworkError && error.payload instanceof ReadableStream) {
            const reader = error.payload.getReader();

            reader
              .read()
              .then(function processText({ done, value }) {
                if (done) {
                  return;
                }

                // Convert stream from Uint8Array to string
                const text = new TextDecoder('utf-8').decode(value);
                const errorResponse = JSON.parse(text);

                if (errorResponse.error === 1014) {
                  setError('SETTINGS_NICKNAME_EXIST');
                } else if (errorResponse.error === 1016) {
                  setError('SETTINGS_SELECTED_NICKNAME');
                } else if (errorResponse.error === 1013) {
                  setError('SETTINGS_CHAR_ACCEPTED');
                } else {
                  setError('ERROR_2001');
                }
              })
              .catch((error) => {
                console.error('Error reading from stream', error);
                setError('ERROR_2001');
              });
          } else {
            setError('ERROR_2001');
          }
        });
    }
  };

  const validateInput = (event: ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault();
    const input = field.current as HTMLInputElement;
    const validityState = input.validity;

    if (validityState.tooShort || validityState.valueMissing) {
      setErrMsgDynamicValues([ScreenNameConstants.SCREEN_NAME_MIN_LENGTH]);
      setError('SETTINGS_NICKNAME_CHAR');
    } else if (validityState.patternMismatch) {
      setErrMsgDynamicValues([]);
      setError('SETTINGS_CHAR_ACCEPTED');
    }
  };

  const resetErrors = (): void => {
    setErrMsgDynamicValues([]);
    setError('');
  };

  const messageContent = (
    <form
      ref={formRef}
      onSubmit={(e): void => {
        e.preventDefault();
        void submitForm();
      }}
    >
      <div>
        <BaseInput
          autoComplete="off"
          minLength={2}
          maxLength={20}
          required
          pattern="[\p{L}\p{N}]*$" // only unicode letters and numbers
          name="screenName"
          inputRef={field}
          errorMessage={error ? <L12nText tk={error} dynamicValues={errMsgDynamicValues} /> : undefined}
          onInvalid={validateInput}
          onFocus={resetErrors}
        />
      </div>
      <div style={{ marginTop: 'var(--ui-spacing-small)' }}>
        <L12nText tk="NICKNAME_MIN_LENGTH_VOL2" />
      </div>
      <div className={styles.popupContent__buttons}>
        <Button tk="STARTPLAYING" type="submit" variant={ButtonVariant.ACCENT} />
      </div>
      <br />
      {showNicknameDisclaimer ? (
        <div className={`${styles.popupContent__info} ${styles.popupContent__smallText}`}>
          <L12nText tk="PERSONAL_DATA" />
        </div>
      ) : null}
    </form>
  );

  return isLoading ? (
    <div style={{ padding: '50px' }}>
      <Loader />
    </div>
  ) : (
    <BlockingModalContent titleTk="ENTER_SCREENNAME" messageComponent={messageContent} />
  );
};
